import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { roleLabel } from '../utils/permissions';
import './GroupsSettings.css';

function GroupsSettings() {
  const { authFetch, user } = useAuth();
  const { confirmElement, requestConfirm } = useConfirm();
  // Managers make and edit groups (Gareth 2026-09-13). Deleting stays with the
  // Super Admin — mirrors api/src/routes/groups.js, which is the real gate.
  const canEdit = ['manager', 'admin'].includes(user?.role);
  const canDelete = user?.role === 'admin';

  const [groups, setGroups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // {..group, members: []}
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', purpose: 'operational' });
  const [message, setMessage] = useState('');

  const fetchGroups = useCallback(async () => {
    const res = await authFetch('/api/v1/groups');
    if (res.ok) {
      const data = (await res.json()).data || [];
      setGroups(data);
      setSelectedId(prev => prev || (data[0] && data[0].group_id) || null);
    }
  }, [authFetch]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    const res = await authFetch(`/api/v1/groups/${id}`);
    if (res.ok) setDetail(await res.json());
  }, [authFetch]);

  const fetchUsers = useCallback(async () => {
    const res = await authFetch('/api/v1/admin/users');
    if (res.ok) {
      const data = (await res.json()).data || [];
      setAllUsers(data.filter(u => u.is_active !== false));
    }
  }, [authFetch]);

  useEffect(() => { fetchGroups(); fetchUsers(); }, [fetchGroups, fetchUsers]);
  useEffect(() => { fetchDetail(selectedId); setShowAdd(false); setSearch(''); }, [selectedId, fetchDetail]);

  const flash = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 2500); };

  const createGroup = async () => {
    if (!newGroup.name.trim()) { flash('Group name is required'); return; }
    const res = await authFetch('/api/v1/groups', { method: 'POST', body: JSON.stringify(newGroup) });
    if (res.ok) {
      const g = await res.json();
      setCreating(false); setNewGroup({ name: '', description: '', purpose: 'operational' });
      await fetchGroups(); setSelectedId(g.group_id); flash('Group created');
    } else { flash('Failed to create group'); }
  };

  const deleteGroup = (g) => {
    requestConfirm({
      title: 'Delete group?',
      message: `Delete the "${g.name}" group? Its membership will be removed.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        const res = await authFetch(`/api/v1/groups/${g.group_id}`, { method: 'DELETE' });
        if (res.ok) { setSelectedId(null); await fetchGroups(); flash('Group deleted'); }
        else { const e = await res.json(); flash(e.message || 'Failed to delete group'); }
      }
    });
  };

  const addMember = async (userId) => {
    const res = await authFetch(`/api/v1/groups/${selectedId}/members`, {
      method: 'POST', body: JSON.stringify({ user_id: userId })
    });
    if (res.ok) { await fetchDetail(selectedId); await fetchGroups(); }
    else { flash('Failed to add member'); }
  };

  const removeMember = async (userId) => {
    const res = await authFetch(`/api/v1/groups/${selectedId}/members/${userId}`, { method: 'DELETE' });
    if (res.ok) { await fetchDetail(selectedId); await fetchGroups(); }
    else { flash('Failed to remove member'); }
  };

  const memberIds = new Set((detail?.members || []).map(m => m.user_id));
  const candidateUsers = allUsers
    .filter(u => !memberIds.has(u.user_id))
    .filter(u => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    });

  return (
    <div className="admin-section groups-settings">
      {confirmElement}
      <div className="groups-header">
        <div>
          <h2>Groups</h2>
          <p className="section-desc">Reusable groups of users for assignment, alerts, on-call and approvals.</p>
        </div>
        {canEdit && !creating && (
          <button className="btn-secondary" onClick={() => setCreating(true)}>+ New Group</button>
        )}
      </div>

      {message && <div className="groups-flash">{message}</div>}

      {creating && (
        <div className="groups-create">
          <input placeholder="Group name" value={newGroup.name}
            onChange={e => setNewGroup({ ...newGroup, name: e.target.value })} />
          <input placeholder="Description (optional)" value={newGroup.description}
            onChange={e => setNewGroup({ ...newGroup, description: e.target.value })} />
          {/* What the group is FOR (2026-09-12). An audience group is a mailing
              list: it must not turn up as a selectable owner of a deal or a
              schedule entry, which is what happened before this existed. */}
          <select
            value={newGroup.purpose}
            onChange={e => setNewGroup({ ...newGroup, purpose: e.target.value })}
            title="What this group is for"
          >
            <option value="operational">Owns work — can be assigned deals and jobs</option>
            <option value="audience">Audience — for sharing and notifying only</option>
          </select>
          <button className="btn-primary" onClick={createGroup}>Create</button>
          <button className="btn-secondary" onClick={() => { setCreating(false); setNewGroup({ name: '', description: '', purpose: 'operational' }); }}>Cancel</button>
        </div>
      )}

      <div className="groups-layout">
        <div className="groups-list">
          {groups.map(g => (
            <button key={g.group_id}
              className={`groups-list-item ${selectedId === g.group_id ? 'active' : ''}`}
              onClick={() => setSelectedId(g.group_id)}>
              <span className="groups-list-name">
                {g.name}
                {g.is_system && <span className="groups-badge">system</span>}
                {/* Audience groups cannot own work, so say so where someone is
                    choosing a group rather than only on the detail panel. */}
                {g.purpose === 'audience' && <span className="groups-badge audience">audience</span>}
              </span>
              <span className="groups-list-count">{g.member_count}</span>
            </button>
          ))}
          {groups.length === 0 && <div className="groups-empty">No groups yet.</div>}
        </div>

        <div className="groups-detail">
          {!detail && <div className="groups-empty">Select a group.</div>}
          {detail && (
            <>
              <div className="groups-detail-head">
                <div>
                  <h3>{detail.name}</h3>
                  {detail.description && <p className="section-desc">{detail.description}</p>}
                  <p className="section-desc">
                    {detail.purpose === 'audience'
                      ? 'For sharing and notifying only — this group cannot be assigned deals or jobs.'
                      : 'Owns work — can be assigned deals, jobs and approvals.'}
                  </p>
                </div>
                {canDelete && !detail.is_system && (
                  <button className="btn-danger btn-small" onClick={() => deleteGroup(detail)}>Delete group</button>
                )}
              </div>

              <div className="groups-members">
                <div className="groups-members-head">
                  <strong>Members ({detail.members.length})</strong>
                  {canEdit && (
                    <button className="btn-secondary btn-small" onClick={() => setShowAdd(v => !v)}>
                      {showAdd ? 'Done' : '+ Add user'}
                    </button>
                  )}
                </div>

                {detail.members.length === 0 && <div className="groups-empty">No members yet.</div>}
                {detail.members.map(m => (
                  <div key={m.user_id} className="groups-member-row">
                    <span>{m.full_name} <span className="groups-member-email">{m.email}</span></span>
                    {canEdit && <button className="btn-link-danger" onClick={() => removeMember(m.user_id)}>Remove</button>}
                  </div>
                ))}
              </div>

              {showAdd && canEdit && (
                <div className="groups-add">
                  <input autoFocus placeholder="Search users to add…" value={search}
                    onChange={e => setSearch(e.target.value)} />
                  <div className="groups-add-list">
                    {candidateUsers.map(u => (
                      <div key={u.user_id} className="groups-add-row">
                        <span>{u.full_name} <span className="groups-member-email">{u.email} · {roleLabel(u.role)}</span></span>
                        <button className="btn-secondary btn-small" onClick={() => addMember(u.user_id)}>Add</button>
                      </div>
                    ))}
                    {candidateUsers.length === 0 && <div className="groups-empty">No matching users.</div>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GroupsSettings;
